import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import _ from 'lodash';
import moment from 'moment';
import { Tag, Select, DatePicker, Switch, Table, Input, Button, Tooltip, notification, Avatar, } from 'antd';
import { CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { Comment } from '@ant-design/compatible';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  businessAtom
} from '../../../atoms/Atoms';
import { dateRangePresets } from '../../utils/utils';
import api from '../../../api/api';
import envConfig from '../../../envConfig';
import { phoneNumber } from '../../utils';
import UserPurchaseProfile from '../../shared-components/UserPurchaseProfile';
import { thousandv2 } from '../../utils/functions';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';

function CustomersByStore() {
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const business = useRecoilValue(businessAtom);
  const [selectedDates, setSelectedDates] = useState({
    start: moment().startOf('week').format('YYYY-MM-DD'),
    end: moment().endOf('week').format('YYYY-MM-DD'),
    isLifetime: false
  });
  const [size, setSize] = useState(20);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function search(queryString, tempPage) {
    if (!loading && business) {
      setLoading(true);
      let url = `reports/customers/store/${business?.id}?page=${page}&size=${size}`;
      if (!selectedDates.isLifetime) {
        const startTime = moment(selectedDates.start).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000');
        const endTime = moment(selectedDates.end).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000');
        url += `&startDate=${startTime}&endDate=${endTime}`;
      }
      if (!queryString?.length > 0) {
        url += `&query=${queryString}`;
      }
      api.get(url)
        .then((response) => {
          if (response.data.success) {
            setData(response.data.data);
          } else {
            showMessage(response.data.message);
          }
        })
        .catch((error) => {
          console.error(error);
          showMessage(error.message);
        })
        .finally(() => setLoading(false));
    }
  }

  function exportUsers() {
    if (!loading && business) {
      setLoading(true);
      let url = `reports/customers/store/${business?.id}/export?`;
      if (!selectedDates.isLifetime) {
        const startTime = moment(selectedDates.start).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000');
        const endTime = moment(selectedDates.end).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000');
        url += `startDate=${startTime}&endDate=${endTime}`;
      }
      if (!query?.length > 0) {
        url += `&query=${query}`;
      }
      api.get(url, { responseType: 'blob' })
        .then((response) => {
          setLoading(false);
          const fileName = `users_${new Date().toLocaleDateString()}.xlsx`;
          const href = URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        })
        .catch((error) => {
          console.error(error);
          showMessage(error.message);
        })
        .finally(() => setLoading(false));
    }
  }

  useEffect(() => {
    setSelectedDates({
      start: moment().startOf('week').format('YYYY-MM-DD'),
      end: moment().endOf('week').format('YYYY-MM-DD'),
      isLifetime: false
    });
  }, []);

  useEffect(() => {
    if (business) {
      setPage(0);
      search(query, 0);
    }
  }, [business, selectedDates]);

  return (
    <div>
      <PageHeader
        className="mb-4 px-0"
        title={t('customers')}
        tags={
          <Tag
            color="#2db7f5"
            icon={business ? <CheckCircleOutlined /> : <SyncOutlined spin />}
          >
            {business ? business.name : `${t('loading')}...`}
          </Tag>
        }
        extra={
          <div className="grid ml-8 md:ml-0 md:flex md:space-x-2 space-y-2">
            <div className="grid md:flex space-y-2">
              {!selectedDates?.isLifetime && (
                <RangePicker
                  className="mt-1"
                  format="YYYY-MM-DD"
                  value={[
                    dayjs(selectedDates?.start),
                    dayjs(selectedDates?.end)
                  ]}
                  allowClear={false}
                  presets={dateRangePresets(t)}
                  onChange={(date, dateString) => {
                    setSelectedDates({
                      ...selectedDates,
                      start: dateString[0],
                      end: dateString[1]
                    });
                  }}
                />
              )}
              <div className="mt-1 mx-auto md:mx-1">
                <span className="mt-2 md:ml-1">{t('show_lifetime')}</span>
                <Switch
                  className="md:ml-1"
                  checked={selectedDates?.isLifetime}
                  onChange={(checked) => {
                    setSelectedDates({
                      ...selectedDates,
                      isLifetime: checked
                    });
                  }}
                />
              </div>
              <Button size="small" type="primary" loading={loading} disabled={data?.total === 0 && !loading} onClick={() => exportUsers()}>
                {t('export')}
              </Button>
            </div>
          </div>
        }
      />
      <Input.Search
        className="w-full mb-1"
        allowClear
        loading={loading}
        enterButton={t('search')}
        onSearch={(value) => {
          setPage(0);
          setQuery(value);
          search(value, 0);
        }}
      />
      <Table
        pagination={{
          pageSize: size,
          showSizeChanger: true,
          defaultCurrent: 0,
          current: page + 1,
          total: data?.total,
          onChange: (tempPage, pageSize) => {
            if (tempPage - 1 !== page) {
              setPage(tempPage - 1);
              search(query, tempPage - 1);
            } else {
              setSize(pageSize);
            }
          }
        }}
        size="small"
        bordered
        rowKey="id"
        loading={loading}
        dataSource={data?.customers}
        columns={[
          {
            title: t('first_name'),
            key: 'firstName',
            align: 'center',
            className: 'text-xs',
            render: (text) => <p>{text.user.firstName}</p>,
          },
          {
            title: t('last_name'),
            key: 'lastName',
            align: 'center',
            className: 'text-xs',
            render: (text) => <p>{text.user.lastName}</p>,
          },
          {
            title: t('email'),
            key: 'email',
            align: 'center',
            className: 'text-xs',
            render: (text) => <p copyable>{text.user.email}</p>,
          },
          {
            title: t('phone'),
            key: 'phoneNumber',
            align: 'center',
            className: 'text-xs',
            render: (value) =>
              <span>
                <p copyable={{ text: value.user.phoneNumber }}>
                  {phoneNumber(value.user.phoneNumber)}
                </p>
              </span>,
          },
          {
            title: t('order_count'),
            key: 'orderCount',
            align: 'center',
            className: 'text-xs',
            render: (text) => <p>{thousandv2(text.orders)}</p>,
          },
          {
            title: t('profile'),
            align: 'center',
            className: 'text-xs',
            render: (text, row) => (
              <UserPurchaseProfile
                userId={row.user.id}
                lifetime
              />
            ),
          }
        ]}
      />
    </div>
  );
}

export default CustomersByStore;
